import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import "../../../assets/css/gcstudent/notes/NotesPage.css";
import NoteDetailPage from "../notesdetail/NoteDetailPage";
import AppContext from "../../../context/AppContext";

const CATEGORIES = [
  "All", "Computer Science", "Artificial Intelligence", "Networking",
  "Software Engineering", "Electronics", "Mathematics", "Physics", "Other",
];

const PAGE_SIZE = 12;

/* ── Per-subject accent ── */
const SUBJECT_ACCENTS = {
  "Computer Science":        "#4F6EF7",
  "Artificial Intelligence": "#10B981",
  "Networking":               "#0EA5E9",
  "Software Engineering":     "#F97316",
  "Electronics":              "#F59E0B",
  "Mathematics":               "#06B6D4",
  "Physics":                   "#EC4899",
  "Other":                     "#64748B",
};
const getAccent = (subject) => SUBJECT_ACCENTS[subject] || SUBJECT_ACCENTS.Other;

const getInitials = (name) => {
  if (!name || name === "Unknown") return "?";
  const parts = name.trim().split(/\s+/);
  const initials = parts.length > 1
    ? parts[0][0] + parts[1][0]
    : parts[0].slice(0, 2);
  return initials.toUpperCase();
};

/* ── Note Card ── */
const NoteCard = ({ note, index, onClick }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const authorName = note.lbteacher?.name || "Unknown";
  const subject = note.subject || "General";
  const accent  = getAccent(subject);

  return (
    <div
      ref={ref}
      className={`gcStudent-note-card ${visible ? "gcStudent-note-visible" : ""}`}
      style={{
        "--note-accent": accent,
        transitionDelay: `${(index % 8) * 0.06}s`,
      }}
      onClick={() => onClick(note)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(note)}
    >
      <div className="gcStudent-note-topbar" />

      <div className="gcStudent-note-body">
        <span className="gcStudent-note-label">Subject</span>
        <span className="gcStudent-note-tag">{subject}</span>

        <h5 className="gcStudent-note-title">{note.title}</h5>
      </div>

      <div className="gcStudent-note-divider" />

      <div className="gcStudent-note-footer">
        <div className="gcStudent-note-author-block">
          <div className="gcStudent-note-avatar">{getInitials(authorName)}</div>
          <div className="gcStudent-note-author-meta">
            <span className="gcStudent-note-author-name">{authorName}</span>
            <span className="gcStudent-note-date">
              {formatDate(note.created_at)}
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="gcStudent-note-view-btn"
        onClick={(e) => { e.stopPropagation(); onClick(note); }}
      >
        View Full Notes
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

/* ── Main Notes Page ── */
const NotesPage = ({ selectedNote, setSelectedNote }) => {
  const { baseUrl } = useContext(AppContext);

  const [notes,          setNotes]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [visible,        setVisible]        = useState(false);
  const [searchQuery,    setSearchQuery]    = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount,   setVisibleCount]   = useState(PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");
    axios.get(`${baseUrl}/general/notes/getAllPublicNotes`)
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setNotes((res.data.notes || []).filter(Boolean));
        } else {
          setError("Could not load notes. Please try again.");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Notes fetch error:", err);
        setError("Something went wrong. Please try again.");
      });
  }, [baseUrl]);

  const filteredNotes = useMemo(() => {
    setVisibleCount(PAGE_SIZE);
    return notes.filter((n) => {
      const matchCat =
        activeCategory === "All" ||
        n.subject === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchSearch =
        n.title?.toLowerCase().includes(q) ||
        n.subject?.toLowerCase().includes(q) ||
        n.lbteacher?.name?.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [notes, activeCategory, searchQuery]);

  const visibleNotes = filteredNotes.slice(0, visibleCount);
  const hasMore      = visibleCount < filteredNotes.length;

  if (selectedNote) {
    return <NoteDetailPage note={selectedNote} onBack={() => setSelectedNote(null)} />;
  }

  return (
    <div className={`gcStudent-notes-page ${visible ? "gcStudent-notes-visible" : ""}`}>
      <div className="gcStudent-notes-blob gcStudent-notes-blob-1" />
      <div className="gcStudent-notes-blob gcStudent-notes-blob-2" />

      <Container fluid="lg" className="gcStudent-notes-content">

        <div className="gcStudent-notes-search-wrap">
          <div className="gcStudent-notes-search-inner">
            <input
              type="text"
              className="gcStudent-notes-search-input"
              placeholder="Search by title, subject or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="gcStudent-notes-search-btn">Search</button>
          </div>
        </div>

        <div className="gcStudent-notes-filter-bar">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`gcStudent-notes-filter-btn ${
                activeCategory === cat ? "gcStudent-notes-filter-active" : ""
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {!loading && !error && (
          <p className="gcStudent-notes-count">
            {filteredNotes.length} note{filteredNotes.length !== 1 ? "s" : ""} found
          </p>
        )}

        {loading && (
  <Row className="g-4">
    {Array.from({ length: PAGE_SIZE }).map((_, i) => (
      <Col key={`skeleton-${i}`} xs={12} sm={6} lg={3}>
        <div className="gcStudent-note-skeleton-card">
          <div className="gcStudent-note-skeleton-topbar" />
          <div className="gcStudent-note-skeleton-body">
            <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-label" />
            <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-tag" />
            <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-title" />
            <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-title-short" />
          </div>
          <div className="gcStudent-note-skeleton-divider" />
          <div className="gcStudent-note-skeleton-footer">
            <div className="gcStudent-note-skeleton-avatar" />
            <div className="gcStudent-note-skeleton-meta">
              <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-name" />
              <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-date" />
            </div>
          </div>
          <div className="gcStudent-note-skeleton-btn" />
        </div>
      </Col>
    ))}
  </Row>
    )}

        {!loading && error && (
          <div className="gcStudent-notes-state-box"><p>{error}</p></div>
        )}

        {!loading && !error && filteredNotes.length === 0 && (
          <div className="gcStudent-notes-state-box">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
              style={{ opacity: 0.3, marginBottom: "10px" }}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/>
              <path d="M14 2v6h6M9 13h6M9 17h4"/>
            </svg>
            <p>No notes found.</p>
          </div>
        )}

        {!loading && !error && visibleNotes.length > 0 && (
          <>
            <Row className="g-4">
              {visibleNotes.map((note, i) => (
                <Col key={note.id} xs={12} sm={6} lg={3}>
                  <NoteCard note={note} index={i} onClick={setSelectedNote} />
                </Col>
              ))}
            </Row>

            {hasMore && (
              <div className="gcStudent-notes-load-more-row">
                <button
                  className="gcStudent-notes-load-more-btn"
                  onClick={() => setVisibleCount((p) => p + PAGE_SIZE)}
                >
                  Load More Notes
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12l7 7 7-7"
                      stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="gcStudent-notes-count-text">
                  Showing {visibleNotes.length} of {filteredNotes.length} notes
                </span>
              </div>
            )}
          </>
        )}

      </Container>
    </div>
  );
};

export default NotesPage;